import { Link } from '@tanstack/react-router';
import type { ReactElement, ReactNode } from 'react';

import { PUBLISH_FOLD_TARGET } from '../constants/hidden-stages';
import type { FunnelLinkTarget } from '../utils/funnel-drill-targets';
import { legacyProductsTarget } from '../utils/run-products-search';

/** DOM id of the Approve & publish pane's "did it land" section — the `publishResults` link's
 *  hash target (and where a `?fStage=DELIVERY` deep link scrolls to). */
export const PUBLISH_RESULTS_ANCHOR = 'funnel-stage-section-deliveryResults';

/** DOM id of the Approve & publish pane's per-product rejections list — the "Rejected" button's
 *  target. */
export const PUBLISH_REJECTIONS_ANCHOR = 'funnel-delivery-rejections';

/**
 * One stage-pane target as a typed `<Link>` element — a plain function (NOT a component) so a
 * wrapping `Button asChild` / chip merges its classes straight onto the real anchor (a component
 * wrapper that doesn't forward props is how these buttons once rendered as plain text).
 */
export function funnelStageLink(
  runId: string,
  target: FunnelLinkTarget,
  children: ReactNode,
  className?: string,
): ReactElement {
  switch (target.kind) {
    case 'catalogProducts':
      // The catalog-wide Products list narrowed to this batch's curation outcome
      // (`curatedInRun` + `outcome`, the pair `GET /catalog-products` filters on).
      return (
        <Link to="/catalog-enrichment/products" search={target.search} className={className}>
          {children}
        </Link>
      );
    case 'content-results':
      return (
        <Link
          to="/catalog-enrichment/runs/$runId/insights"
          params={{ runId }}
          className={className}
        >
          {children}
        </Link>
      );
    case 'publishResults':
      // The combined Approve & publish pane's "did it land" section on Summary (Publish folded
      // into Approval 2026-10-16) — same anchors as before, on the combined pane.
      return (
        <Link
          to="/catalog-enrichment/runs/$runId"
          params={{ runId }}
          search={(prev) => ({ ...prev, fStage: PUBLISH_FOLD_TARGET })}
          hash={
            target.section === 'rejections' ? PUBLISH_REJECTIONS_ANCHOR : PUBLISH_RESULTS_ANCHOR
          }
          className={className}
        >
          {children}
        </Link>
      );
    case 'route':
      return (
        <Link to={target.to} params={{ runId }} search={target.search} className={className}>
          {children}
        </Link>
      );
    case 'products':
    default: {
      // Old one-route Products searches resolve to the route that owns that view now, so the
      // link's href is the real destination (no redirect hop).
      const dest = legacyProductsTarget(target.search);
      if (dest?.to === 'review') {
        return (
          <Link
            to="/catalog-enrichment/runs/$runId/gate"
            params={{ runId }}
            search={dest.search}
            className={className}
          >
            {children}
          </Link>
        );
      }
      if (dest?.to === 'listings') {
        return (
          <Link
            to="/catalog-enrichment/runs/$runId/products/listings"
            params={{ runId }}
            search={dest.search}
            className={className}
          >
            {children}
          </Link>
        );
      }
      if (dest?.to === 'skipped') {
        return (
          <Link
            to="/catalog-enrichment/runs/$runId/products/skipped"
            params={{ runId }}
            search={dest.search}
            className={className}
          >
            {children}
          </Link>
        );
      }
      return (
        <Link
          to="/catalog-enrichment/runs/$runId/products"
          params={{ runId }}
          search={dest?.search ?? target.search}
          className={className}
        >
          {children}
        </Link>
      );
    }
  }
}
